
import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, User, LogOut, Settings, Shield, Home, Activity } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useSidebar } from '@/components/ui/sidebar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { t, language } = useLanguage();
  const { user, isAuthenticated, logout, isAdmin } = useAuth();
  const { theme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { toggleSidebar } = useSidebar();
  const isHomePage = location.pathname === '/';

  // Get the display name (from user_metadata, or use email as fallback)
  const displayName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'User';
  const avatarInitials = displayName.substring(0, 2).toUpperCase() || 'U';
  
  // Fermer le menu déroulant quand on clique à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Fonction de déconnexion sécurisée
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      toast.error("Erreur lors de la déconnexion. Veuillez réessayer.");
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-card border-b border-border/50 shadow-sm">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleSidebar}
              aria-label="Toggle sidebar"
              className="mr-2"
            >
              <Menu size={20} />
            </Button>
            
            <Link to="/" className="text-foreground font-medium tracking-tight flex items-center transition-opacity hover:opacity-90">
              <img 
                src={theme === 'dark' ? '/lovable-uploads/a0f0939a-3b33-4cf7-8f8d-85504e9ad117.png' : '/lovable-uploads/ee1ae56d-3fb6-45ea-9a24-8cfa75082425.png'} 
                alt="Relut" 
                className="h-9 w-auto object-contain"
              />
            </Link>
          </div>

          {/* Right Side - User account menu or login/signup buttons */}
          <div className="flex items-center space-x-2">
            {isAuthenticated ? (
              <div ref={dropdownRef}>
                <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={user?.user_metadata?.avatar_url || ""} alt={displayName} />
                        <AvatarFallback>{avatarInitials}</AvatarFallback>
                      </Avatar>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56" align="end" forceMount>
                    <div className="flex flex-col space-y-1 p-2">
                      <p className="text-sm font-medium">{displayName}</p>
                      <p className="text-xs text-muted-foreground">{user?.email}</p>
                    </div>
                    <DropdownMenuSeparator />
                    
                    {isAdmin ? (
                      <>
                        <DropdownMenuItem asChild>
                          <Link to="/admin" className="flex items-center">
                            <Shield className="h-4 w-4 mr-2" />
                            {t('sidebar.dashboard')}
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/admin?tab=channels" className="flex items-center">
                            <Activity className="h-4 w-4 mr-2" />
                            {t('sidebar.manageChannels')}
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/admin?tab=users" className="flex items-center">
                            <User className="h-4 w-4 mr-2" />
                            {t('sidebar.manageUsers')}
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/admin?tab=licenses" className="flex items-center">
                            <Settings className="h-4 w-4 mr-2" />
                            {t('sidebar.manageLicenses')}
                          </Link>
                        </DropdownMenuItem>
                      </>
                    ) : (
                      <DropdownMenuItem asChild>
                        <Link to="/dashboard" className="flex items-center">
                          <Home className="h-4 w-4 mr-2" />
                          {t('sidebar.dashboard')}
                        </Link>
                      </DropdownMenuItem>
                    )}
                    
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="text-red-500 cursor-pointer">
                      <LogOut className="h-4 w-4 mr-2" />
                      {language === 'fr' ? 'Déconnexion' : 'Logout'}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ) : (
              <>
                <Button variant="secondary" size="sm" asChild>
                  <Link to="/auth/login">{language === 'fr' ? 'Se connecter' : 'Login'}</Link>
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};
